import React, { useState, useEffect, useCallback } from "react";
import {
  Layout,
  Card,
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Statistic,
  Input,
  Modal,
  Avatar,
  Divider,
  Spin,
  Steps,
  Tooltip,
} from "antd";
import {
  FaSearch,
  FaCheckCircle,
  FaClock,
  FaPlus,
  FaCalendarAlt,
  FaDoorOpen,
  FaBuilding,
  FaTimesCircle,
  FaHourglassHalf,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { MdMeetingRoom, MdSchedule, MdLocationOn } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Api from "../../api/Api";
import dayjs from "dayjs";
import "dayjs/locale/pt";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(isSameOrBefore);
dayjs.locale("pt");

const { Content } = Layout;
const { Title, Text } = Typography;

/* ─────────────────────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────────────────────── */
const formatDateTime = (str) => {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleString("pt-PT", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return str; }
};

const formatTime = (str) => {
  if (!str) return "—";
  try {
    return new Date(str).toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
  } catch { return str; }
};

const DURACOES = [
  { label: "30 min", minutos: 30 },
  { label: "1h", minutos: 60 },
  { label: "1h 30min", minutos: 90 },
  { label: "2h", minutos: 120 },
  { label: "4h", minutos: 240 },
  { label: "6h", minutos: 360 },
];

const DIAS_PT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const MESES_PT = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

/* ─────────────────────────────────────────────────────────────────────────────
   MINI CALENDAR
───────────────────────────────────────────────────────────────────────────── */
const MiniCalendar = ({ diasDisponiveis, selectedDate, onSelectDate }) => {
  const firstAvailable = diasDisponiveis.length > 0 ? dayjs(diasDisponiveis[0].data) : dayjs();
  const lastAvailable = diasDisponiveis.length > 0
    ? dayjs(diasDisponiveis[diasDisponiveis.length - 1].data)
    : dayjs();

  const [viewMonth, setViewMonth] = useState(() => firstAvailable.startOf("month"));

  const datasComSlots = new Set(
    diasDisponiveis
      .filter((d) => d.slots?.some((s) => s.status === "livre"))
      .map((d) => d.data)
  );
  const datasNoRange = new Set(diasDisponiveis.map((d) => d.data));

  const diasNoMes = viewMonth.daysInMonth();
  const primeiroDiaSemana = viewMonth.startOf("month").day();

  const canGoPrev = viewMonth.subtract(1, "month").isSameOrBefore(
    firstAvailable.startOf("month"), "month"
  ) === false;
  const canGoNext = viewMonth.add(1, "month").isSameOrBefore(
    lastAvailable.startOf("month"), "month"
  );

  const cells = [];
  for (let i = 0; i < primeiroDiaSemana; i++) cells.push(null);
  for (let d = 1; d <= diasNoMes; d++) cells.push(d);

  const dateStr = (d) => viewMonth.date(d).format("YYYY-MM-DD");
  const isSelected = (d) => d && selectedDate === dateStr(d);
  const hasSlots = (d) => d && datasComSlots.has(dateStr(d));
  const isPast = (d) => d && viewMonth.date(d).isBefore(dayjs(), "day");
  const inRange = (d) => d && datasNoRange.has(dateStr(d));
  const isToday = (d) => d && viewMonth.date(d).isSame(dayjs(), "day");

  return (
    <div style={{ userSelect: "none" }}>
      {/* Navegação */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <Button
          type="text" size="small" icon={<FaChevronLeft style={{ fontSize: 11 }} />}
          onClick={() => setViewMonth((m) => m.subtract(1, "month"))}
          disabled={!canGoPrev}
          style={{ color: canGoPrev ? "#1677ff" : "#d9d9d9" }}
        />
        <Text strong style={{ fontSize: 14 }}>
          {MESES_PT[viewMonth.month()]} {viewMonth.year()}
        </Text>
        <Button
          type="text" size="small" icon={<FaChevronRight style={{ fontSize: 11 }} />}
          onClick={() => setViewMonth((m) => m.add(1, "month"))}
          disabled={!canGoNext}
          style={{ color: canGoNext ? "#1677ff" : "#d9d9d9" }}
        />
      </div>

      {/* Cabeçalho dias da semana */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 6 }}>
        {DIAS_PT.map((dia) => (
          <div key={dia} style={{ textAlign: "center", fontSize: 10, fontWeight: 600, color: "#8c8c8c", padding: "2px 0" }}>
            {dia}
          </div>
        ))}
      </div>

      {/* Células */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
        {cells.map((d, idx) => {
          if (!d) return <div key={`e-${idx}`} />;

          const sel = isSelected(d);
          const slots = hasSlots(d);
          const past = isPast(d);
          const today = isToday(d);
          const range = inRange(d);
          const clickable = slots && !past;

          return (
            <Tooltip
              key={d}
              title={
                clickable
                  ? `${d} — Horários disponíveis`
                  : past ? "Data passada" : !range ? "" : "Sem horários livres"
              }
              placement="top"
            >
              <div
                onClick={() => clickable && onSelectDate(dateStr(d))}
                style={{
                  position: "relative",
                  textAlign: "center",
                  padding: "7px 2px",
                  borderRadius: 7,
                  background: sel ? "#1677ff" : "transparent",
                  color: sel ? "#fff" : (!range || past) ? "#c0c0c0" : today ? "#1677ff" : "#1f1f1f",
                  border: sel
                    ? "1.5px solid #1677ff"
                    : today && !sel
                    ? "1.5px solid #1677ff"
                    : "1.5px solid transparent",
                  cursor: clickable ? "pointer" : "default",
                  opacity: (!range || past) ? 0.45 : 1,
                  fontSize: 12,
                  fontWeight: sel || today ? 700 : 400,
                  transition: "all 0.12s",
                }}
              >
                {d}
                {slots && !past && !sel && (
                  <div style={{
                    position: "absolute", bottom: 2, left: "50%",
                    transform: "translateX(-50%)",
                    width: 4, height: 4, borderRadius: "50%", background: "#1677ff",
                  }} />
                )}
              </div>
            </Tooltip>
          );
        })}
      </div>

      {/* Legenda */}
      <div style={{ display: "flex", gap: 14, marginTop: 12, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{ width: 6, height: 6, borderRadius: "50%", background: "#1677ff" }} />
          <Text type="secondary" style={{ fontSize: 10 }}>Com horários livres</Text>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{ width: 12, height: 12, borderRadius: 3, background: "#1677ff" }} />
          <Text type="secondary" style={{ fontSize: 10 }}>Selecionado</Text>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div style={{ width: 12, height: 12, borderRadius: 3, border: "1.5px solid #1677ff", background: "transparent" }} />
          <Text type="secondary" style={{ fontSize: 10 }}>Hoje</Text>
        </div>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   MODAL NOVA SOLICITAÇÃO
───────────────────────────────────────────────────────────────────────────── */
const SolicitacaoModal = ({ open, onClose, onSuccess, userStored }) => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [escritorios, setEscritorios] = useState([]);
  const [selectedEscritorio, setSelectedEscritorio] = useState(null);

  const [salas, setSalas] = useState([]);
  const [loadingSalas, setLoadingSalas] = useState(false);
  const [selectedSala, setSelectedSala] = useState(null);

  const [diasDisponiveis, setDiasDisponiveis] = useState([]);
  const [loadingHorarios, setLoadingHorarios] = useState(false);

  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDuracao, setSelectedDuracao] = useState(null);

  useEffect(() => {
    if (open) { resetAll(); loadEscritorios(); }
  }, [open]);

  const resetAll = () => {
    setStep(0);
    setSelectedEscritorio(null); setSelectedSala(null);
    setSelectedDate(null); setSelectedSlot(null); setSelectedDuracao(null);
    setSalas([]); setDiasDisponiveis([]);
  };

  const loadEscritorios = async () => {
    try {
      setLoading(true);
      const res = await Api.ListEscritorio();
      setEscritorios(Array.isArray(res) ? res : []);
    } catch { toast.error("Erro ao carregar escritórios."); }
    finally { setLoading(false); }
  };

  const handleEscritorioSelect = async (e) => {
    setSelectedEscritorio(e);
    setSelectedSala(null); setSelectedDate(null); setSelectedSlot(null); setSelectedDuracao(null); setDiasDisponiveis([]);
    try {
      setLoadingSalas(true);
      const res = await Api.ListSala({ id_escritorio: e.id });
      setSalas(Array.isArray(res) ? res : []);
      setStep(1);
    } catch { toast.error("Erro ao carregar salas."); }
    finally { setLoadingSalas(false); }
  };

  const handleSalaSelect = async (sala) => {
    setSelectedSala(sala);
    setSelectedDate(null); setSelectedSlot(null); setSelectedDuracao(null);
    try {
      setLoadingHorarios(true);
      const res = await Api.ListHorarioLogicaSala({ id_sala: sala.id });
      // Suporte a { success, data: [...] } OU array direto
      const raw = res?.data ?? res;
      setDiasDisponiveis(Array.isArray(raw) ? raw : []);
      setStep(2);
    } catch { toast.error("Erro ao carregar horários."); }
    finally { setLoadingHorarios(false); }
  };

  const slotsDodia = selectedDate
    ? (diasDisponiveis.find((d) => d.data === selectedDate)?.slots ?? [])
    : [];

  // Verifica se há slots livres contíguos suficientes a partir do slot escolhido (cada slot = 30 min)
  const slotsSuficientes = (slotInicio, duracaoMin) => {
    if (!slotInicio || !duracaoMin) return false;
    const needed = duracaoMin / 30;
    const idx = slotsDodia.findIndex((s) => s.inicio === slotInicio);
    if (idx < 0) return false;
    for (let i = idx; i < idx + needed; i++) {
      if (!slotsDodia[i] || slotsDodia[i].status === "ocupado") return false;
    }
    return true;
  };

  const calcSaida = (dataStr, inicio, min) =>
    dayjs(`${dataStr} ${inicio}`).add(min, "minute");

  const handleSubmit = async () => {
    if (!selectedDate || !selectedSlot || !selectedDuracao) {
      toast.warning("Selecione uma data, horário e duração.");
      return;
    }
    if (!slotsSuficientes(selectedSlot.inicio, selectedDuracao.minutos)) {
      toast.warning("Slots insuficientes para essa duração a partir do horário selecionado.");
      return;
    }
    try {
      setSubmitting(true);
      await Api.AddLogisticaSala({
        entrada: `${selectedDate} ${selectedSlot.inicio}:00`,
        saida: calcSaida(selectedDate, selectedSlot.inicio, selectedDuracao.minutos).format("YYYY-MM-DD HH:mm:ss"),
        id_sala: selectedSala.id,
        id_user: userStored.id,
      });
      toast.success("Solicitação enviada! Aguardando aprovação.");
      onSuccess(); onClose();
    } catch { toast.error("Erro ao enviar solicitação."); }
    finally { setSubmitting(false); }
  };

  return (
    <Modal
      open={open} onCancel={onClose} width={720}
      title={
        <Space>
          <MdMeetingRoom style={{ color: "#1677ff", fontSize: 22 }} />
          <span style={{ fontWeight: 600, fontSize: 18 }}>Nova Solicitação de Sala</span>
        </Space>
      }
      footer={
        step === 2
          ? [
              <Button key="back" onClick={() => setStep(1)}>Voltar</Button>,
              <Button
                key="submit" type="primary" onClick={handleSubmit} loading={submitting}
                disabled={!selectedDate || !selectedSlot || !selectedDuracao}
                style={{ borderRadius: 6, fontWeight: 500 }}
              >
                Confirmar Solicitação
              </Button>,
            ]
          : [<Button key="cancel" onClick={onClose}>Cancelar</Button>]
      }
      bodyStyle={{ padding: "24px" }}
      destroyOnClose
    >
      <Steps
        current={step} size="small" style={{ marginBottom: 28 }}
        items={[{ title: "Escritório" }, { title: "Sala" }, { title: "Data & Horário" }]}
      />

      {/* STEP 0 */}
      {step === 0 && (
        <Spin spinning={loading}>
          <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
            Selecione o escritório desejado:
          </Text>
          <Row gutter={[16, 16]}>
            {escritorios.map((e) => (
              <Col span={12} key={e.id}>
                <Card hoverable onClick={() => handleEscritorioSelect(e)}
                  style={{ borderRadius: 12, border: "2px solid #f0f0f0", cursor: "pointer" }}
                  bodyStyle={{ padding: "20px 16px", textAlign: "center" }}>
                  <FaBuilding style={{ fontSize: 32, color: "#1677ff", marginBottom: 12 }} />
                  <div style={{ fontWeight: 600, fontSize: 15 }}>{e.descricao}</div>
                </Card>
              </Col>
            ))}
          </Row>
        </Spin>
      )}

      {/* STEP 1 */}
      {step === 1 && (
        <Spin spinning={loadingSalas}>
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary">Escritório: <strong>{selectedEscritorio?.descricao}</strong></Text>
            <Button type="link" size="small" onClick={() => setStep(0)} style={{ paddingLeft: 8 }}>Trocar</Button>
          </div>
          <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>Selecione a sala:</Text>
          <Row gutter={[16, 16]}>
            {salas.map((s) => (
              <Col span={12} key={s.id}>
                <Card hoverable onClick={() => handleSalaSelect(s)}
                  style={{ borderRadius: 12, border: "2px solid #f0f0f0", cursor: "pointer" }}
                  bodyStyle={{ padding: "20px 16px", textAlign: "center" }}>
                  <MdMeetingRoom style={{ fontSize: 32, color: "#1677ff", marginBottom: 12 }} />
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{s.descricao}</div>
                </Card>
              </Col>
            ))}
          </Row>
        </Spin>
      )}

      {/* STEP 2 */}
      {step === 2 && (
        <Spin spinning={loadingHorarios}>
          {/* Breadcrumb */}
          <div style={{ marginBottom: 16, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              <MdLocationOn style={{ marginRight: 4, verticalAlign: "middle" }} />
              {selectedEscritorio?.descricao} &rsaquo; <strong>{selectedSala?.descricao}</strong>
            </Text>
            <Button type="link" size="small" onClick={() => setStep(1)} style={{ padding: 0 }}>
              Trocar sala
            </Button>
          </div>

          <Row gutter={20}>
            {/* Calendário */}
            <Col xs={24} md={13}>
              <Divider orientation="left" style={{ fontSize: 12, color: "#8c8c8c", marginTop: 0 }}>
                <FaCalendarAlt style={{ marginRight: 6 }} /> Escolha o dia
              </Divider>
              <MiniCalendar
                diasDisponiveis={diasDisponiveis}
                selectedDate={selectedDate}
                onSelectDate={(date) => {
                  setSelectedDate(date);
                  setSelectedSlot(null);
                  setSelectedDuracao(null);
                }}
              />
            </Col>

            {/* Horários + Duração */}
            <Col xs={24} md={11}>
              {!selectedDate ? (
                <div style={{
                  height: "100%", display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  color: "#d9d9d9", paddingTop: 40, gap: 10,
                }}>
                  <FaCalendarAlt style={{ fontSize: 40 }} />
                  <Text type="secondary" style={{ textAlign: "center", fontSize: 13 }}>
                    Selecione um dia<br />no calendário
                  </Text>
                </div>
              ) : (
                <>
                  <Divider orientation="left" style={{ fontSize: 12, color: "#8c8c8c", marginTop: 0 }}>
                    <FaClock style={{ marginRight: 6 }} />
                    {dayjs(selectedDate).format("DD [de] MMMM")}
                  </Divider>

                  {slotsDodia.length === 0 ? (
                    <Text type="secondary" style={{ fontSize: 13 }}>
                      Sem horários disponíveis neste dia.
                    </Text>
                  ) : (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 16 }}>
                      {slotsDodia.map((slot) => {
                        const livre = slot.status === "livre";
                        const sel = selectedSlot?.inicio === slot.inicio;
                        return (
                          <button
                            key={slot.inicio}
                            disabled={!livre}
                            onClick={() => {
                              if (!livre) return;
                              setSelectedSlot(slot);
                              setSelectedDuracao(null);
                            }}
                            style={{
                              padding: "5px 11px", borderRadius: 14,
                              border: sel ? "2px solid #1677ff" : livre ? "1.5px solid #d9d9d9" : "1.5px solid #ffa39e",
                              background: sel ? "#e6f4ff" : livre ? "#fff" : "#fff1f0",
                              color: sel ? "#1677ff" : livre ? "#1f1f1f" : "#ff7875",
                              fontWeight: sel ? 700 : 400,
                              cursor: livre ? "pointer" : "not-allowed",
                              fontSize: 12,
                              transition: "all 0.12s",
                              display: "flex", alignItems: "center", gap: 4,
                            }}
                          >
                            {livre
                              ? <FaClock style={{ fontSize: 9, opacity: 0.5 }} />
                              : <FaTimesCircle style={{ fontSize: 9 }} />
                            }
                            {slot.inicio}
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {selectedSlot && (
                    <>
                      <Divider orientation="left" style={{ fontSize: 12, color: "#8c8c8c" }}>
                        <MdSchedule style={{ marginRight: 6 }} /> Duração
                      </Divider>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {DURACOES.map((d) => {
                          const sel = selectedDuracao?.minutos === d.minutos;
                          const ok = slotsSuficientes(selectedSlot.inicio, d.minutos);
                          return (
                            <Tooltip key={d.minutos} title={!ok ? "Slots insuficientes para essa duração" : ""}>
                              <button
                                disabled={!ok}
                                onClick={() => ok && setSelectedDuracao(d)}
                                style={{
                                  padding: "5px 13px", borderRadius: 14,
                                  border: sel ? "2px solid #1677ff" : ok ? "1.5px solid #d9d9d9" : "1.5px solid #f0f0f0",
                                  background: sel ? "#e6f4ff" : ok ? "#fff" : "#fafafa",
                                  color: sel ? "#1677ff" : ok ? "#1f1f1f" : "#c0c0c0",
                                  fontWeight: sel ? 700 : 400,
                                  cursor: ok ? "pointer" : "not-allowed",
                                  fontSize: 12,
                                  transition: "all 0.12s",
                                }}
                              >
                                {d.label}
                              </button>
                            </Tooltip>
                          );
                        })}
                      </div>
                    </>
                  )}
                </>
              )}
            </Col>
          </Row>

          {/* Resumo */}
          {selectedDate && selectedSlot && selectedDuracao && (
            <div style={{
              marginTop: 20, background: "#f6ffed", border: "1px solid #b7eb8f",
              borderRadius: 10, padding: "12px 16px",
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <FaCheckCircle style={{ color: "#52c41a", fontSize: 18, flexShrink: 0 }} />
              <div>
                <Text strong style={{ display: "block" }}>
                  {dayjs(selectedDate).format("DD/MM/YYYY")} &nbsp;·&nbsp;
                  {selectedSlot.inicio} → {calcSaida(selectedDate, selectedSlot.inicio, selectedDuracao.minutos).format("HH:mm")}
                </Text>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {selectedSala?.descricao} &nbsp;·&nbsp; {selectedDuracao.label}
                </Text>
              </div>
            </div>
          )}
        </Spin>
      )}
    </Modal>
  );
};

/* ─────────────────────────────────────────────────────────────────────────────
   GESTÃO SALA PAGE
───────────────────────────────────────────────────────────────────────────── */
const GestaoSala = () => {
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const userStored = JSON.parse(localStorage.getItem("user")) || {};

  useEffect(() => {
    document.title = "Gestão de Salas | Dashboard";
    loadReservas();
  }, []);

  const loadReservas = useCallback(async () => {
    try {
      setLoading(true);
      const res = await Api.ListLogisticaSala();
      setReservas(Array.isArray(res) ? res : []);
    } catch {
      toast.error("Erro ao carregar reservas.");
      setReservas([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const filtered = reservas.filter((r) => {
    const q = search.toLowerCase();
    return !q || r.solicitante?.toLowerCase().includes(q) ||
      r.sala?.toLowerCase().includes(q) || r.escritorio?.toLowerCase().includes(q);
  });

  const countHoje = reservas.filter((r) => dayjs(r.entrada).isSame(dayjs(), "day")).length;
  const countSalas = [...new Set(reservas.map((r) => r.id_sala))].length;

  const columns = [
    {
      title: "Sala / Escritório", key: "sala", width: 260,
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <Avatar shape="square" size="large"
            style={{ backgroundColor: "#e6f4ff", color: "#1677ff", borderRadius: 8 }}
            icon={<MdMeetingRoom />} />
          <div>
            <div style={{ fontWeight: 600, color: "#1f1f1f", marginBottom: 2 }}>{record.sala}</div>
            <Text type="secondary" style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 4 }}>
              <FaBuilding /> {record.escritorio}
            </Text>
          </div>
        </div>
      ),
    },
    {
      title: "Solicitante", dataIndex: "solicitante", key: "solicitante", width: 180,
      render: (nome) => (
        <Space>
          <Avatar size="small" style={{ backgroundColor: "#1677ff" }}>{nome?.[0]}</Avatar>
          <Text>{nome}</Text>
        </Space>
      ),
    },
    {
      title: "Entrada", key: "entrada", width: 160,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text style={{ fontSize: 13 }}>
            <FaCalendarAlt style={{ marginRight: 6, color: "#8c8c8c" }} />
            {dayjs(r.entrada).format("DD/MM/YYYY")}
          </Text>
          <Text type="secondary" style={{ fontSize: 12, marginLeft: 20 }}>{formatTime(r.entrada)}</Text>
        </Space>
      ),
    },
    {
      title: "Saída", key: "saida", width: 100,
      render: (_, r) => <Text style={{ fontSize: 13, color: "#8c8c8c" }}>{formatTime(r.saida)}</Text>,
    },
    {
      title: "Data Aprovação", dataIndex: "data_aprovacao", key: "data_aprovacao", width: 160,
      render: (v) => <Text style={{ fontSize: 13 }}>{v ? formatDateTime(v) : "—"}</Text>,
    },
    {
      title: "Estado", dataIndex: "aprovado", key: "aprovado", width: 120,
      render: (aprovado) =>
        aprovado === "S" ? (
          <Tag color="success" icon={<FaCheckCircle />}
            style={{ borderRadius: 20, padding: "4px 12px", fontWeight: 500 }}>Aprovado</Tag>
        ) : (
          <Tag color="warning" icon={<FaHourglassHalf />}
            style={{ borderRadius: 20, padding: "4px 12px", fontWeight: 500 }}>Pendente</Tag>
        ),
    },
  ];

  return (
    <Layout style={{ background: "transparent" }}>
      <Content style={{ padding: "24px 32px" }}>
        <ToastContainer position="top-right" autoClose={4000} />

        <div style={{
     background: "linear-gradient(135deg, #1677ff 0%, #4096ff 100%)",
          borderRadius: 16, padding: "24px 32px", marginBottom: 32,
          boxShadow: "0 8px 20px rgba(15, 76, 129, 0.2)",
        }}>
          <Row align="middle" justify="space-between">
            <Col>
              <Space size="middle">
                <div style={{
                  backgroundColor: "rgba(255,255,255,0.2)", borderRadius: 12, padding: 10,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <MdMeetingRoom style={{ fontSize: 32, color: "#fff" }} />
                </div>
                <div>
                  <Title level={2} style={{ color: "#fff", margin: 0, fontWeight: 700 }}>
                    Gestão de Salas
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 16 }}>
                    Reservas aprovadas e agendamentos de salas de reunião
                  </Text>
                </div>
              </Space>
            </Col>
            <Col>
              <Button type="primary" size="large" icon={<FaPlus />} onClick={() => setModalOpen(true)}
                style={{
                  backgroundColor: "#fff", color: "#0f4c81", border: "none",
                  fontWeight: 600, borderRadius: 30, padding: "0 24px", height: 48,
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}>
                Nova Solicitação
              </Button>
            </Col>
          </Row>
        </div>

        <Row gutter={[24, 24]} style={{ marginBottom: 32 }}>
          {[
            { title: "Total de Reservas", value: reservas.length, color: "#1677ff", bg: "#e6f4ff", icon: <MdMeetingRoom style={{ fontSize: 24, color: "#1677ff" }} /> },
            { title: "Reservas Hoje", value: countHoje, color: "#52c41a", bg: "#f6ffed", icon: <FaCalendarAlt style={{ fontSize: 24, color: "#52c41a" }} /> },
            { title: "Salas Utilizadas", value: countSalas, color: "#722ed1", bg: "#f9f0ff", icon: <FaDoorOpen style={{ fontSize: 24, color: "#722ed1" }} /> },
          ].map((s) => (
            <Col xs={24} sm={8} key={s.title}>
              <Card bordered={false}
                style={{ borderRadius: 16, boxShadow: "0 4px 12px rgba(0,0,0,0.05)", border: "1px solid #f0f0f0" }}
                bodyStyle={{ padding: "20px 24px" }}>
                <Statistic
                  title={<span style={{ fontSize: 14, color: "#8c8c8c" }}>{s.title}</span>}
                  value={s.value}
                  valueStyle={{ fontSize: 32, fontWeight: 700, color: s.color }}
                  prefix={<div style={{ backgroundColor: s.bg, borderRadius: 12, padding: 10, marginRight: 16 }}>{s.icon}</div>}
                />
              </Card>
            </Col>
          ))}
        </Row>

        <Card bordered={false}
          style={{ borderRadius: 16, marginBottom: 24, boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}
          bodyStyle={{ padding: "16px 24px" }}>
          <Input size="large" prefix={<FaSearch style={{ color: "#bfbfbf" }} />}
            placeholder="Pesquisar por solicitante, sala ou escritório..."
            value={search} onChange={(e) => setSearch(e.target.value)}
            allowClear style={{ borderRadius: 30, maxWidth: 500 }} />
        </Card>

        <Card bordered={false}
          style={{ borderRadius: 16, boxShadow: "0 4px 16px rgba(0,0,0,0.05)", overflow: "hidden" }}
          bodyStyle={{ padding: 0 }}>
          <Table
            columns={columns} dataSource={filtered} rowKey="id" loading={loading}
            pagination={{
              pageSize: 10, showSizeChanger: true,
              showTotal: (total) => `Total de ${total} reservas`,
              style: { padding: "16px 24px" },
            }}
            locale={{ emptyText: "Nenhuma reserva encontrada" }}
            scroll={{ x: 900 }}
          />
        </Card>

        <SolicitacaoModal
          open={modalOpen} onClose={() => setModalOpen(false)}
          onSuccess={loadReservas} userStored={userStored}
        />
      </Content>
    </Layout>
  );
};

export default GestaoSala;